import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Box, Grid } from '@mui/material';
import { IconButton, Tooltip } from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';
import { useThemeToggle } from '../hooks/useThemeToggle';
import { useCustomMediaQuery } from '../hooks/useMediaQuery';

import {
    completeTour,
    resetTour,
    selectIsSkipped,
    selectTourStatus,
    selectTutorialPreference,
    startTour,
    TOUR_TYPES,
    goToNextTour,
    updateTourStep,
} from 'store/slices/tour.slice';
import Industry from './Settings/Industry';
import Security from './Settings/Security';
import POSIntegration from './Settings/PointOfSale';
import { FullWidthTabs, Typography } from '../shared';
import { PROFILE_SETTINGS } from '../utils/constants';
import JoyrideTour from './Onboarding/Tour/JoyRideTour';
import { CompanyInformation } from './Settings/CompanyInformation';
import { PersonalInformation } from './Settings/PersonalInformation';
import Billing from './Settings/Billing';

const makeStyles = ({ isScreenLowerThan1024 }) => ({
    root: {
        p: isScreenLowerThan1024 ? '0px' : '24px',
        height: 'calc(100vh - 69px)',
        justifyContent: isScreenLowerThan1024 ? 'flex-end' : 'space-between',
    },
    settingContainer: {
        displayed: 'flex',
        flexDirection: 'column',
        backgroundColor: (theme) => theme.backgroundColor.primary,
    },
    settingOptions: {
        display: 'flex',
        alignItems: 'center',
        p: '20px 16px',
        borderRadius: '12px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: (theme) => theme.backgroundColor.secondary,
        },
        '& p': {
            fontSize: '14px',
        },
    },
    selectedOption: {
        backgroundColor: (theme) => theme.backgroundColor.profileSettings,
        '& .profileIcon': {
            backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#6C727F' : '#F9FAFB'),
        },
        '& p': {
            fontSize: '14px',
            fontWeight: 700,
            color: (theme) => theme.textColor.dark,
        },
        '& path, & circle, & rect, & polygon, & line, & polyline, & ellipse': {
            transition: 'fill 0.3s, stroke 0.3s',
        },
        //&.selected rect
        '&.selected path, &.selected circle, &.selected polygon, &.selected line, &.selected polyline, &.selected ellipse':
            {
                stroke: (theme) =>
                    theme.palette.mode === 'dark' ? theme.backgroundColor.light : '',
            },
    },
});

const tabs = [
    { label: 'Personal Information', content: <PersonalInformation /> },
    { label: 'Point of Sale', content: <POSIntegration /> },
    {
        label: 'Company Information',
        content: (
            <CompanyInformation
                showIndustry={false}
                showCompanyHeader={true}
                companyLabel="Company Name"
                timezoneLabel={'Time Zone'}
                industryLabel={'Industry'}
                typeOfEntityLabel={'Type of Entity'}
            />
        ),
    },
    {
        label: 'Industry',
        content: <Industry />,
    },
    {
        label: 'Security',
        content: <Security />,
    },
    {
        label: 'Billing',
        content: <Billing />,
    },
];

export const Profile = () => {
    const { themeMode } = useThemeToggle();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const companyData = useSelector((state) => state.company.data);
    const hasPurchaseAccess = companyData?.is_purchase_reconciler;
    const hasInvoiceAccess = companyData?.is_invoice_reconciler;

    const filteredProfileSettings = PROFILE_SETTINGS.filter((setting) => {
        if (setting.name === 'Billing' && !hasPurchaseAccess) {
            return false;
        }
        if (setting.name === 'Point of Sale' && !hasPurchaseAccess && !hasInvoiceAccess) {
            return false;
        }
        return true;
    });

    const filteredTabs = tabs.filter((tab) => {
        if (tab.label === 'Billing' && !hasPurchaseAccess) {
            return false;
        }
        if (tab.label === 'Point of Sale' && !hasPurchaseAccess && !hasInvoiceAccess) {
            return false;
        }
        return true;
    });

    const [selectedProfileIndex, setSelectedProfileIndex] = useState(() => {
        const section = params.section || 'personal';
        return filteredProfileSettings.findIndex(
            (setting) => setting.name.toLowerCase().replace(/\s+/g, '-') === section,
        );
    });

    const isScreenLowerThan1024 = useCustomMediaQuery('(max-width: 1025px)');
    const styles = makeStyles({ isScreenLowerThan1024 });
    const dispatch = useDispatch();

    const [copied, setCopied] = useState(false);
    const tourId = TOUR_TYPES.EMAIL_FORWARDING;
    const nextTourId = TOUR_TYPES.SKYBOX_INTEGRATION;
    const tourStatus = useSelector((state) => selectTourStatus(state, tourId));
    const tutorialPreference = useSelector((state) => selectTutorialPreference(state));
    const isSkipped = useSelector((state) => selectIsSkipped(state, tourId));
    const [runTour, setRunTour] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const nextTourStatus = useSelector((state) => selectTourStatus(state, nextTourId));

    const emailFilterContent =
        '({"mobile ticket"} OR {"authentication"} OR {"code"} OR {"alert"} OR {"purchase"} OR {"mobile tickets"} OR {"confirmation"} OR {"ticket request"} OR {"billets"} OR {"Print your "} OR {"Ticket Delivery"} OR {"all set to see"} OR {"Print at Home"} OR {"Enclosed"} OR {"Download your tickets"} OR {"Thank You"} OR {"Thank you"} OR {"Tickets have arrived"} OR {"E-Tickets are attached"} OR {"Your E-Tickets, Print Immediately"} OR {"Ticketfly ETicket"} OR {"Order"} OR {"cash"} OR {"ready to print"} OR {"E-Tickets"} OR {"etickets"} OR {"eticket"} OR {"Receipt"} OR {"A quick reminder about"} OR {"your" AND "tickets"} OR {"you" AND "tickets"} OR {"ticket" AND "transaction"} OR {"Print"} OR {"purchases"} OR {"ticket"} OR {"E-Ticket"} OR {"Ticketfly"} OR {"print"} OR {"orders"} OR {"reminder"} OR {"concerts"} OR {"attached"} OR {"concert"} OR {"verify"} OR {"password"} OR {"reset"} OR {"Confirmed"})';

    const handleCopyFilter = async () => {
        try {
            await navigator.clipboard.writeText(emailFilterContent);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const steps = [
        {
            target: '[data-tour="auto-email-field"]',
            content: (
                <div>
                    <p>
                        Use this email inbox to set up automatic email forwarding. For detailed
                        instructions on how to set up email forwarding, please refer to this{' '}
                        <a
                            href="https://circlerhelp.blogspot.com/2025/04/how-to-set-up-email-forwarding-in.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1976d2', textDecoration: 'underline' }}
                        >
                            article
                        </a>
                        .
                    </p>

                    <p>
                        Here's an example of an auto-forwarding filter. Feel free to modify it to
                        better suit your business needs:
                    </p>
                    <div style={{ position: 'relative' }}>
                        <pre
                            style={{
                                backgroundColor: themeMode === 'dark' ? '#2d3748' : '#f7fafc',
                                padding: '12px',
                                paddingRight: '50px',
                                borderRadius: '6px',
                                overflowX: 'auto',
                                whiteSpace: 'pre-wrap',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                marginTop: '8px',
                            }}
                        >
                            {emailFilterContent}
                        </pre>

                        <Tooltip title={copied ? 'Copied!' : 'Copy filter'} arrow>
                            <IconButton
                                onClick={handleCopyFilter}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '8px',
                                    backgroundColor:
                                        themeMode === 'dark'
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        backgroundColor:
                                            themeMode === 'dark'
                                                ? 'rgba(255,255,255,0.2)'
                                                : 'rgba(0,0,0,0.1)',
                                    },
                                    width: '32px',
                                    height: '32px',
                                }}
                            >
                                {copied ? (
                                    <Check sx={{ fontSize: 16, color: '#4caf50' }} />
                                ) : (
                                    <ContentCopy
                                        sx={{
                                            fontSize: 16,
                                            color: themeMode === 'dark' ? '#fff' : '#666',
                                        }}
                                    />
                                )}
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            ),
            placement: 'left',
            disableBeacon: false,
        },
    ];

    const handleSelectedProfile = (index) => {
        const section = filteredProfileSettings[index].name.toLowerCase().replace(/\s+/g, '-');
        navigate(`/profile/${section}`);
        setSelectedProfileIndex(index);
    };

    useEffect(() => {
        const section = params.section || 'personal';
        const newIndex = filteredProfileSettings.findIndex(
            (setting) => setting.name.toLowerCase().replace(/\s+/g, '-') === section,
        );
        if (newIndex !== -1) {
            setSelectedProfileIndex(newIndex);
        } else if (section === 'billing' && !hasPurchaseAccess) {
            navigate('/profile/personal-settings');
            setSelectedProfileIndex(0);
        }
    }, [params.section, filteredProfileSettings, hasPurchaseAccess, navigate]);

    useEffect(() => {
        const manualRestart = tourStatus?.manuallyStarted;

        if (tutorialPreference === 'skipped' || isSkipped) {
            setRunTour(false);
        }

        if (manualRestart) {
            setCurrentStep(0);
            navigate('/profile/company-information');
            dispatch(startTour({ tourId }));
            setRunTour(true);
        } else if (!tourStatus?.completed && !manualRestart) {
            if (tutorialPreference === 'skipped' || isSkipped) {
                setRunTour(false);
                return;
            }
            const timer = setTimeout(() => {
                navigate('/profile/company-information');
                setRunTour(true);
                if (tourStatus?.started) {
                    setCurrentStep(tourStatus.currentStep);
                } else {
                    dispatch(startTour({ tourId }));
                    setCurrentStep(0);
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [dispatch, tourStatus?.completed, tourStatus?.manuallyStarted, location.state?.restartTour]);

    const renderSelectedUserSettings = (settingId) => {
        const selectedSetting = filteredProfileSettings[settingId];
        if (!selectedSetting) {
            return <div>No component selected</div>;
        }

        switch (selectedSetting.name) {
            case 'Personal Settings':
                return <PersonalInformation />;
            case 'Point of Sale':
                return <POSIntegration />;
            case 'Company Information':
                return (
                    <CompanyInformation
                        showIndustry={false}
                        showCompanyHeader={true}
                        companyLabel="Company Name"
                        timezoneLabel={'Time Zone'}
                        industryLabel={'Industry'}
                        typeOfEntityLabel={'Type of Entity'}
                    />
                );
            case 'Industry':
                return <Industry />;
            case 'Security':
                return <Security />;
            case 'Billing':
                return <Billing />;
            default:
                return <div>No component selected</div>;
        }
    };

    return (
        <>
            <JoyrideTour
                steps={steps}
                run={runTour}
                stepIndex={currentStep}
                continuous
                scrollToFirstStep
                showBeacon={true}
                spotlightClicks={true}
                offset={50}
                onStepBefore={(idx) => {
                    setCurrentStep(idx);
                    dispatch(updateTourStep({ tourId, stepIndex: idx }));
                }}
                onFinish={() => {
                    setRunTour(false);
                    dispatch(completeTour({ tourId }));

                    if (tutorialPreference === 'skipped' || isSkipped) {
                        setRunTour(false);
                        return;
                    }

                    if (!nextTourStatus?.completed) {
                        dispatch(
                            goToNextTour({
                                currentTourId: tourId,
                                nextTourId: nextTourId,
                            }),
                        );
                        setTimeout(() => {
                            navigate('/profile/point-of-sale');
                        }, 1000);
                    } else {
                        dispatch(completeTour({ tourId }));
                    }
                }}
            />
            <Grid container spacing={2} sx={styles.root}>
                <Grid
                    container
                    item
                    xs={12}
                    md={isScreenLowerThan1024 ? 12 : 3}
                    lg={isScreenLowerThan1024 ? 12 : 3}
                    sx={styles.settingContainer}
                    spacing={2}
                    p={isScreenLowerThan1024 ? '24px 0px 24px 14px' : '24px !important'}
                    overflow={'auto'}
                >
                    {isScreenLowerThan1024 ? (
                        <Grid
                            item
                            xs={12}
                            sx={{
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark' ? '#121212' : '#F4F4F6',
                                p: 1,
                                m: '8px 0px 8px 8px',
                            }}
                        >
                            <FullWidthTabs
                                tabs={filteredTabs}
                                variant={'scrollable'}
                                tabCustomStyles={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    textTransform: 'capitalize',
                                }}
                            />
                        </Grid>
                    ) : (
                        filteredProfileSettings.map((setting, index) => {
                            return (
                                <Grid
                                    item
                                    key={index}
                                    onClick={() => handleSelectedProfile(index)}
                                    sx={{
                                        ...styles.settingOptions,
                                        ...(selectedProfileIndex === index &&
                                            styles.selectedOption),
                                    }}
                                    className={selectedProfileIndex === index ? 'selected' : ''}
                                >
                                    <Grid item>
                                        <Box
                                            className="profileIcon"
                                            sx={{
                                                borderRadius: '1000px',
                                                width: '48px',
                                                height: '48px',
                                                padding: '12px',
                                                backgroundColor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? '#394150'
                                                        : '#F9FAFB',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {themeMode === 'dark'
                                                ? setting.darkModeIcon
                                                : setting.icon}
                                        </Box>
                                    </Grid>
                                    &nbsp;&nbsp;
                                    <Grid item>
                                        <Typography
                                            text={setting.name}
                                            customStyles={{
                                                color: (theme) => theme.textColor.primary,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            );
                        })
                    )}
                </Grid>

                {!isScreenLowerThan1024 ? (
                    <Grid item xs={12} md={9} lg={9} p={'40px 60px !important'}>
                        {renderSelectedUserSettings(selectedProfileIndex)}
                    </Grid>
                ) : (
                    <></>
                )}
            </Grid>
        </>
    );
};
